import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { api } from "../services/api";

const ConstraintContext = createContext();

export const useConstraints = () => {
  const context = useContext(ConstraintContext);
  if (!context) {
    throw new Error("useConstraints must be used within a ConstraintProvider");
  }
  return context;
};

export const ConstraintProvider = ({ children, isLoggedIn }) => {
  const [constraints, setConstraints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchConstraints = useCallback(async () => {
    // Double check token existence
    if (!isLoggedIn && !localStorage.getItem("adminUser")) return;

    setLoading(true);
    try {
      const res = await api.getConstraints();
      setConstraints(res.data.constraints || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching constraints:", err);
      // Only set error if we actually have a token (otherwise it's expected 401)
      if (localStorage.getItem("adminUser")) {
        setError("Failed to load constraints");
      }
      // Don't break the app if constraints fail to load
      setConstraints([]);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchConstraints();
    } else {
      setConstraints([]);
    }
  }, [isLoggedIn, fetchConstraints]);

  const addConstraint = async (constraint) => {
    try {
      const res = await api.addConstraint(constraint);
      if (res.data.success) {
        // Refetch to get the full constraint object with _id
        await fetchConstraints();
        return { success: true };
      }
      return { success: false, message: res.data.message };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.error || "Failed to add constraint",
      };
    }
  };

  const updateConstraint = async (id, updatedData) => {
    try {
      const res = await api.updateConstraint(id, updatedData);
      if (res.data.success) {
        await fetchConstraints();
        return { success: true };
      }
      return { success: false, message: res.data.message };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.error || "Failed to update constraint",
      };
    }
  };

  const deleteConstraint = async (id) => {
    try {
      const res = await api.deleteConstraint(id);
      if (res.data.success) {
        setConstraints((prev) => prev.filter((c) => c._id !== id));
        return { success: true };
      }
      return { success: false, message: res.data.message };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.error || "Failed to delete constraint",
      };
    }
  };

  const getConstraintsByScope = (scope) => {
    return constraints.filter((c) => c.scope === scope);
  };

  const value = {
    constraints,
    loading,
    error,
    fetchConstraints,
    addConstraint,
    updateConstraint,
    deleteConstraint,
    getConstraintsByScope,
  };

  return (
    <ConstraintContext.Provider value={value}>
      {children}
    </ConstraintContext.Provider>
  );
};
